import React from 'react';
import { motion } from 'framer-motion';
import type { AnalyticsSummary } from '../types';
import { useDelayedAnimation } from '../hooks/useDelayedAnimation';

interface SlaGaugeProps {
    summary?: AnalyticsSummary;
    isLoading: boolean;
}

const CX = 70;
const CY = 68;
const RADIUS = 54;
const STROKE_W = 10;
const HALF_CIRC = Math.PI * RADIUS;

const halfArcPath = (
    cx: number,
    cy: number,
    r: number,
    startDeg: number,
    endDeg: number,
): string => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const x1 = cx + r * Math.cos(toRad(startDeg));
    const y1 = cy + r * Math.sin(toRad(startDeg));
    const x2 = cx + r * Math.cos(toRad(endDeg));
    const y2 = cy + r * Math.sin(toRad(endDeg));
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
};

export const SlaGauge: React.FC<SlaGaugeProps> = ({ summary, isLoading }) => {
    const { animated } = useDelayedAnimation(!isLoading && !!summary, {
        delay: 120,
    });

    if (isLoading || !summary) {
        return (
            <div className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-4 h-[176px] animate-pulse" />
        );
    }

    const actual = summary.overallUptime;
    const target = summary.slaTarget;
    const met = actual >= target;

    const fillFrac = Math.min(1, Math.max(0, actual / 100));

    const dashFill = animated ? fillFrac * HALF_CIRC : 0;
    const dashGap = HALF_CIRC - dashFill;

    const targetFrac = target / 100;
    const targetDeg = -180 + targetFrac * 180;
    const targetX = CX + RADIUS * Math.cos((targetDeg * Math.PI) / 180);
    const targetY = CY + RADIUS * Math.sin((targetDeg * Math.PI) / 180);

    const fillColor = met ? 'var(--status-up)' : 'var(--status-down)';
    const textColor = met ? 'var(--status-up-text)' : 'var(--status-down-text)';
    const badgeBg = met ? 'var(--status-up-bg)' : 'var(--status-down-bg)';

    const delta = actual - target;
    const deltaLabel = met
        ? `+${Math.abs(delta).toFixed(3)}% выше цели`
        : `${delta.toFixed(3)}% ниже цели`;

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-4"
        >
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-[13px] font-medium text-[var(--text)]">SLA</h3>
                <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ color: textColor, background: badgeBg }}
                >
                    {met ? 'Выполняется' : 'Нарушено'}
                </span>
            </div>

            <div className="flex flex-col items-center">
                <svg
                    width={140}
                    height={86}
                    viewBox="0 0 140 86"
                    className="overflow-visible"
                    aria-label={`SLA: ${actual.toFixed(2)}% из ${target}%`}
                >
                    <path
                        d={halfArcPath(CX, CY, RADIUS, -180, 0)}
                        fill="none"
                        stroke="var(--border)"
                        strokeWidth={STROKE_W}
                        strokeLinecap="round"
                    />

                    <path
                        d={halfArcPath(CX, CY, RADIUS, -180, 0)}
                        fill="none"
                        stroke={fillColor}
                        strokeWidth={STROKE_W}
                        strokeLinecap="round"
                        strokeDasharray={`${dashFill} ${dashGap + 0.01}`}
                        style={{ transition: 'stroke-dasharray 1.1s cubic-bezier(.4,0,.2,1)' }}
                    />

                    <circle
                        cx={targetX}
                        cy={targetY}
                        r={5}
                        fill="var(--bg-elevated)"
                        stroke="var(--status-warn)"
                        strokeWidth={2}
                    />

                    <text
                        x={CX}
                        y={CY + 6}
                        textAnchor="middle"
                        fontSize={16}
                        fontWeight={600}
                        style={{ fill: fillColor, fontFamily: 'var(--font-mono, ui-monospace, monospace)' }}
                    >
                        {actual.toFixed(2)}%
                    </text>
                </svg>

                <div className="flex items-center gap-1.5 -mt-1.5">
                    <span className="w-2 h-2 rounded-full bg-[var(--status-warn)]" />
                    <span className="text-[11px] text-[var(--text-muted)]">Цель: {target}%</span>
                </div>

                <p className="text-[12px] font-medium mt-1.5" style={{ color: textColor }}>
                    {deltaLabel}
                </p>

                <div className="flex items-center gap-4 mt-3 text-center">
                    <div>
                        <div className="text-[11px] font-mono font-semibold text-[var(--status-up)]">{actual.toFixed(2)}%</div>
                        <div className="text-[9px] text-[var(--text-muted)] mt-0.5">Работал</div>
                    </div>
                    <div className="w-px h-6 bg-[var(--border)]" />
                    <div>
                        <div className="text-[11px] font-mono font-semibold" style={{ color: (100 - actual) > 0.1 ? 'var(--status-down)' : 'var(--text-muted)' }}>
                            {(100 - actual).toFixed(3)}%
                        </div>
                        <div className="text-[9px] text-[var(--text-muted)] mt-0.5">Простой</div>
                    </div>
                    <div className="w-px h-6 bg-[var(--border)]" />
                    <div>
                        <div className="text-[11px] font-mono font-semibold text-[var(--text-secondary)]">
                            {summary.sitesMonitored}
                        </div>
                        <div className="text-[9px] text-[var(--text-muted)] mt-0.5">Сайтов</div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};